import clsx from 'clsx';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import { Label } from '../ui/label';
import PlusIcon from '@/assets/icons/plus.svg?react';
import HorizontalLine from '../ui/HorizontalLine';
import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import StyledDropZone from '../styled/StyledDropZone';
import { useCallback, useEffect, useState } from 'react';
import AddTransportBenefitModal from './modals/AddTransportBenefitModal';
import CircuitEditorialStep from './CircuitEditorialStep';
import AddMealBenefitModal from './modals/AddMealBenefitModal';
import AddAccommodationBenefitModal from './modals/AddAccommodationBenefitModal';
import { CircuitBenefitType } from '@/types/circuits/CircuitBenefitType';
import AddExcursionBenefitModal from './modals/AddExcursionBenefitModal';
import { useCircuitEditorialModal } from '@/hooks/useCircuitEditorialModal';
import { createCircuitDay } from '@/utils/circuits/createEmptyCircuitDays';
import { useIsCircuitDayEmpty } from '@/hooks/useIsCircuitDayEmpty';
import CircuitEditorialDayImport from './CircuitEditorialDayImport';
import { EditorialMode } from './CircuitDaysList';
import { useTranslation } from '@/i18n';
import { parseCircuitBenefitType } from '@/utils/circuits/parseCircuitBenefitType';
import { findNextAvailableStepIndex } from '@/utils/circuits/associatedBenefits/findNextAvailableStepIndex';
import { getAccommodationTargetStepIndex } from '@/utils/circuits/associatedBenefits/getAccommodationTargetStepIndex';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import StyledTranslatableInput from '../styled/StyledTranslatableInput';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableTextarea from '../styled/StyledTranslatableTextarea';
import TranslateIcon from '../translation/TranslateIcon';
import { useTranslateFields } from '@/hooks/useTranslate';
import Modal from '../ui/Modal';
import { ParagraphOverrides } from '@/api/circuits.api';
import { useParagraphOverrideHighlight } from '@/hooks/useParagraphOverrideHighlight';

interface CircuitEditorialFormProps {
  control: Control<CircuitFormDataWithTranslations>;
  index: number;
  setDisplayedDayIndex: React.Dispatch<React.SetStateAction<number>>;
  days: CircuitFormDataWithTranslations['circuitDays'];
  mode: EditorialMode;
  language: SupportedLocale;
  isViewMode?: boolean;
  paragraphOverrides?: ParagraphOverrides;
  suppressedOverrideFields?: Set<string>;
}

const CircuitEditorialForm = ({
  control,
  index,
  days,
  setDisplayedDayIndex,
  mode,
  language,
  isViewMode = false,
  paragraphOverrides,
  suppressedOverrideFields,
}: CircuitEditorialFormProps) => {
  const { t } = useTranslation();

  const [warningField, setWarningField] = useState<string | null>(null);
  const [acknowledgedFields, setAcknowledgedFields] = useState<Set<string>>(
    new Set(),
  );

  const { setValue } = useFormContext<CircuitFormDataWithTranslations>();
  const { hasDayOverride } = useParagraphOverrideHighlight({
    paragraphOverrides,
    language,
    control,
    suppressedOverrideFields,
  });

  // Subscribe to this day's fields so the red wrapper updates on every keystroke.
  const liveDayTitle = useWatch({
    control,
    name: `translations.${language}.circuitDays.${index}.stepTitle`,
  });
  const liveDayDescription = useWatch({
    control,
    name: `translations.${language}.circuitDays.${index}.description`,
  });
  const liveDayTitleBase = useWatch({
    control,
    name: `circuitDays.${index}.stepTitle`,
  });
  const liveDayDescriptionBase = useWatch({
    control,
    name: `circuitDays.${index}.description`,
  });

  const showTitleOverride = hasDayOverride(`dayTitle_${index}`);
  const showDescriptionOverride = hasDayOverride(`dayDescription_${index}`);

  const handleDayFieldFocus = (key: string) => {
    if (hasDayOverride(key) && !acknowledgedFields.has(key)) {
      setWarningField(key);
    }
  };

  const handleDayModalCancel = () => {
    setWarningField(null);
  };

  const handleDayModalClose = () => {
    if (warningField) {
      setAcknowledgedFields((prev) => new Set(prev).add(warningField));
    }
    setWarningField(null);
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: `circuitDays.${index}.steps`,
  });

  const { translateFields, isTranslating } = useTranslateFields({
    control,
    setValue,
    fields: [
      `circuitDays.${index}.stepTitle`,
      `circuitDays.${index}.description`,
    ],
    targetLanguage: language,
  });

  const [transportModalStepIndex, setTransportModalStepIndex] = useState<
    number | null
  >(null);

  const [mealModalStepIndex, setMealModalStepIndex] = useState<number | null>(
    null,
  );

  const [accommodationModalStepIndex, setAccommodationModalStepIndex] =
    useState<number | null>(null);

  const [activityModalStepIndex, setActivityModalStepIndex] = useState<
    number | null
  >(null);

  const nbDays = useWatch({ control, name: 'nbDays' });

  const {
    addBenefits,
    lastAccommodationStepIndex,
    lastActivityStepIndex,
    lastMealStepIndex,
    lastTransportStepIndex,
    maxBenefitStepIndex,
    steps,
  } = useCircuitEditorialModal({
    index,
    days,
  });

  const isCircuitDayEmpty = useIsCircuitDayEmpty(days[index]);

  useEffect(() => {
    if (maxBenefitStepIndex + 1 === fields.length) {
      append(createCircuitDay().steps[0]);
    }
  }, [append, fields.length, maxBenefitStepIndex]);

  const openAddedBenefitModal = useCallback(
    (type: CircuitBenefitType, stepIndex: number) => {
      switch (type) {
        case 'Transport':
          setTransportModalStepIndex(stepIndex);
          break;
        case 'Repas':
          setMealModalStepIndex(stepIndex);
          break;
        case 'Hébergements':
          setAccommodationModalStepIndex(stepIndex);
          break;
        case 'Activités':
          setActivityModalStepIndex(stepIndex);
          break;
      }
    },
    [],
  );

  return (
    <div
      className="flex flex-col gap-4 w-full relative"
      id={`circuit-day-${index}`}
    >
      {mode === 'list' && (
        <h1 className="text-lg font-medium text-black">
          {t('circuits.editorial.day', { index: index + 1 })}
        </h1>
      )}
      <Label className="text-base">{t('circuits.editorial.steps')}</Label>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <BenefitToAdd
          type="Transport"
          onClick={() => {
            setTransportModalStepIndex(
              findNextAvailableStepIndex(lastTransportStepIndex + 1, steps),
            );
          }}
          isViewMode={isViewMode}
        />
        <BenefitToAdd
          type="Activités"
          onClick={() => {
            setActivityModalStepIndex(
              findNextAvailableStepIndex(lastActivityStepIndex + 1, steps),
            );
          }}
          isViewMode={isViewMode}
        />
        <BenefitToAdd
          type="Repas"
          onClick={() => {
            setMealModalStepIndex(
              findNextAvailableStepIndex(lastMealStepIndex + 1, steps),
            );
          }}
          isViewMode={isViewMode}
        />
        <BenefitToAdd
          type="Hébergements"
          onClick={() => {
            setAccommodationModalStepIndex(lastAccommodationStepIndex + 1);
          }}
          isViewMode={isViewMode}
        />
      </div>
      {fields.map((field, stepIndex) => (
        <CircuitEditorialStep
          key={field.id}
          control={control}
          index={index}
          stepIndex={stepIndex}
          openStepModal={(type) => openAddedBenefitModal(type, stepIndex)}
          removeStep={() => remove(stepIndex)}
          maxBenefitStepIndex={maxBenefitStepIndex}
          days={days}
          isViewMode={isViewMode}
          language={language}
        />
      ))}
      <HorizontalLine />
      {mode === 'day' && !isViewMode && isCircuitDayEmpty && (
        <CircuitEditorialDayImport
          index={index}
          daysLength={days.length}
          addBenefits={addBenefits}
          setDisplayedDayIndex={setDisplayedDayIndex}
        />
      )}
      {mode === 'day' && (
        <>
          <div className="flex gap-2 items-center">
            <Label className="text-base">
              {t('circuits.editorial.editorial')}
            </Label>
            <TranslateIcon
              translateFields={() => translateFields({})}
              isTranslating={isTranslating}
              language={language}
              hidden={isViewMode}
            />
          </div>
          <div
            className={clsx({
              'bg-red-50 rounded-lg ring-1 ring-red-200 p-1': showTitleOverride,
            })}
            data-override-sync={`${liveDayTitle ?? ''}|${liveDayTitleBase ?? ''}`}
            onFocus={() => handleDayFieldFocus(`dayTitle_${index}`)}
          >
            <StyledTranslatableInput
              originalName={`circuitDays.${index}.stepTitle`}
              control={control}
              name={`circuitDays.${index}.stepTitle`}
              setValue={setValue}
              language={language}
              title={t('circuits.editorial.stepTitle')}
              placeholder={t('circuits.editorial.stepTitlePlaceholder')}
              disabled={isViewMode}
            />
          </div>
          <StyledDropZone
            control={control}
            name={`circuitDays.${index}.images`}
            title={t('circuits.editorial.images')}
            placeholder={t('circuits.editorial.imagesPlaceholder')}
            multiple
            disabled={isViewMode}
          />
          <div
            className={clsx({
              'bg-red-50 rounded-lg ring-1 ring-red-200 p-1':
                showDescriptionOverride,
            })}
            data-override-sync={`${liveDayDescription ?? ''}|${liveDayDescriptionBase ?? ''}`}
            onFocus={() => handleDayFieldFocus(`dayDescription_${index}`)}
          >
            <StyledTranslatableTextarea
              originalName={`circuitDays.${index}.description`}
              control={control}
              name={`circuitDays.${index}.description`}
              setValue={setValue}
              language={language}
              title={t('circuits.editorial.description')}
              placeholder={t('circuits.editorial.descriptionPlaceholder')}
              rows={3}
              disabled={isViewMode}
            />
          </div>
          {warningField && (
            <Modal
              closeModal={handleDayModalCancel}
              title={t('circuits.paragraphOverridesWarning.title')}
              message={t('circuits.paragraphOverridesWarning.message')}
              actionTitle={t('common.continue')}
              actionOnClick={handleDayModalClose}
              hasAction
              cancelBtnLabel={t('common.cancel')}
            >
              {(paragraphOverrides?.[warningField] ?? []).length > 0 && (
                <ul className="mt-2 text-sm text-gray-700 list-disc pl-5">
                  {(paragraphOverrides?.[warningField] ?? []).map(
                    (entry, i) => (
                      <li key={i}>
                        {entry.distributorName}
                        {entry.entityType === 'editableCircuit'
                          ? ` — ${entry.entityRef}`
                          : ` — ${t('circuits.paragraphOverridesWarning.estimateRef', { ref: entry.entityRef })}`}
                      </li>
                    ),
                  )}
                </ul>
              )}
            </Modal>
          )}
        </>
      )}
      {transportModalStepIndex !== null && (
        <AddTransportBenefitModal
          index={index}
          stepIndex={transportModalStepIndex}
          circuitControl={control}
          addBenefits={({ standardBenefit, optionalBenefits, stepNbDays }) => {
            const isModification =
              transportModalStepIndex !== lastTransportStepIndex + 1;
            addBenefits({
              standardBenefit,
              optionalBenefits,
              type: 'Transport',
              stepIndex: transportModalStepIndex,
              stepNbDays: isModification
                ? stepNbDays
                : Math.min(stepNbDays ?? 1, (nbDays ?? days.length) - index),
              stepId: steps[transportModalStepIndex].transport.id,
              isModification,
            });
          }}
          standardBenefit={
            steps[transportModalStepIndex].transport.standardBenefit
          }
          optionalBenefits={
            steps[transportModalStepIndex].transport.optionalBenefits
          }
          closeModal={() => {
            setTransportModalStepIndex(null);
          }}
          isViewMode={isViewMode}
        />
      )}
      {mealModalStepIndex !== null && (
        <AddMealBenefitModal
          index={index}
          stepIndex={mealModalStepIndex}
          circuitControl={control}
          addBenefits={({ standardBenefit, optionalBenefits }) =>
            addBenefits({
              standardBenefit,
              optionalBenefits,
              type: 'Repas',
              stepIndex: mealModalStepIndex,
              stepId: steps[mealModalStepIndex].meals.id,
            })
          }
          standardBenefit={steps[mealModalStepIndex].meals.standardBenefit}
          optionalBenefits={steps[mealModalStepIndex].meals.optionalBenefits}
          closeModal={() => {
            setMealModalStepIndex(null);
          }}
          isViewMode={isViewMode}
        />
      )}
      {accommodationModalStepIndex !== null && (
        <AddAccommodationBenefitModal
          index={index}
          stepIndex={accommodationModalStepIndex}
          circuitControl={control}
          addBenefits={({
            standardBenefit,
            optionalBenefits,
            standardBenefitPensionType,
            optionalBenefitsPensionTypes,
            stepNbDays,
          }) => {
            const isModification =
              accommodationModalStepIndex !== lastAccommodationStepIndex + 1;
            const targetStepIndex = isModification
              ? accommodationModalStepIndex
              : getAccommodationTargetStepIndex(
                  accommodationModalStepIndex,
                  steps,
                  standardBenefit,
                );
            addBenefits({
              standardBenefit,
              optionalBenefits,
              standardBenefitPensionType,
              optionalBenefitsPensionTypes,
              type: 'Hébergements',
              stepIndex: targetStepIndex,
              stepNbDays: isModification
                ? stepNbDays
                : Math.min(stepNbDays ?? 1, (nbDays ?? days.length) - index),
              stepId: steps[targetStepIndex].accommodations.id,
              isModification,
            });
          }}
          standardBenefit={
            steps[accommodationModalStepIndex].accommodations.standardBenefit
          }
          optionalBenefits={
            steps[accommodationModalStepIndex].accommodations.optionalBenefits
          }
          standardBenefitPensionType={
            steps[accommodationModalStepIndex].accommodations
              .standardBenefitPensionType
          }
          optionalBenefitsPensionTypes={
            steps[accommodationModalStepIndex].accommodations
              .optionalBenefitsPensionTypes
          }
          closeModal={() => {
            setAccommodationModalStepIndex(null);
          }}
          isViewMode={isViewMode}
        />
      )}
      {activityModalStepIndex !== null && (
        <AddExcursionBenefitModal
          index={index}
          stepIndex={activityModalStepIndex}
          circuitControl={control}
          addBenefits={({ standardBenefit, optionalBenefits }) =>
            addBenefits({
              standardBenefit,
              optionalBenefits,
              type: 'Activités',
              stepIndex: activityModalStepIndex,
              stepId: steps[activityModalStepIndex].activities.id,
            })
          }
          standardBenefit={
            steps[activityModalStepIndex].activities.standardBenefit
          }
          optionalBenefits={
            steps[activityModalStepIndex].activities.optionalBenefits
          }
          closeModal={() => setActivityModalStepIndex(null)}
          isViewMode={isViewMode}
        />
      )}
    </div>
  );
};

export default CircuitEditorialForm;

export const BenefitToAdd = ({
  type,
  onClick,
  isViewMode = false,
}: {
  type: CircuitBenefitType;
  onClick: () => void;
  isViewMode?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={clsx(
        'flex items-center justify-between p-3 rounded-lg w-full',
        {
          'bg-[#FFEBEB]': type === 'Transport',
          'bg-[#F3EBFF]': type === 'Activités',
          'bg-[#EBF6FF]': type === 'Repas',
          'bg-[#EBFFEF]': type === 'Hébergements',
        },
      )}
    >
      <Label className="text-base">
        {t(
          `circuits.editorial.benefitTypes.${parseCircuitBenefitType(type).toLowerCase()}`,
        )}
      </Label>
      {!isViewMode && (
        <StyledTooltipContainer
          tooltipMessage={t('circuits.editorial.tooltip.add')}
        >
          <PlusIcon className="size-5 fill-black" onClick={onClick} />
        </StyledTooltipContainer>
      )}
    </div>
  );
};
